import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { JamInventraLogo } from '../components/JamInventraLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Box,
  Warehouse,
  Layers,
  ArrowLeftRight,
  ShoppingCart,
  BarChart3,
  Users,
  Settings,
  CreditCard,
  FileText,
  Menu,
  X,
  ChevronDown,
  LogOut,
  User,
  Crown,
  Truck,
  ShoppingBag,
  MapPin,
  FileCode,
  BookOpen,
  Shield,
  UserCheck,
  ClipboardList,
  Lock,
  AlertTriangle
} from 'lucide-react';

const getNavItems = (userRole) => {
  // Super Admin sees only platform-level pages
  if (userRole === 'super_admin') {
    return [
      { path: '/app/admin', label: 'Super Admin', icon: Crown },
      { path: '/app/system-specs', label: 'System Specs', icon: FileCode },
      { path: '/app/training-manual', label: 'Training Manual', icon: BookOpen },
      { path: '/app/security', label: 'Security', icon: Shield },
    ];
  }

  const items = [
    { path: '/app/sales', label: 'Sales', icon: ShoppingCart },
    { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/app/items', label: 'Items', icon: Box },
    { path: '/app/warehouses', label: 'Warehouses', icon: Warehouse },
    { path: '/app/storage-locations', label: 'Locations', icon: MapPin },
    { path: '/app/inventory', label: 'Inventory', icon: Layers },
    { path: '/app/transactions', label: 'Transactions', icon: ArrowLeftRight },
    { path: '/app/customers', label: 'Customers', icon: UserCheck },
    { path: '/app/purchase-orders', label: 'Purchase Orders', icon: Truck },
    { path: '/app/sales-orders', label: 'Sales Orders', icon: ShoppingBag },
    { path: '/app/proforma-invoices', label: 'Proforma Invoices', icon: ClipboardList },
    { path: '/app/reports', label: 'Reports', icon: BarChart3 },
    { path: '/app/users', label: 'Users', icon: Users },
    { path: '/app/audit-log', label: 'Audit Log', icon: FileText },
    { path: '/app/settings', label: 'Settings', icon: Settings },
    { path: '/app/billing', label: 'Billing', icon: CreditCard },
    { path: '/app/system-specs', label: 'System Specs', icon: FileCode },
    { path: '/app/training-manual', label: 'Training Manual', icon: BookOpen },
    { path: '/app/security', label: 'Security', icon: Shield },
  ];
  
  return items;
};

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accessStatus, setAccessStatus] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await api.get('/subscription/access-check');
        setAccessStatus(res.data);
      } catch {
        setAccessStatus({ has_access: true });
      } finally {
        setAccessLoading(false);
      }
    };
    checkAccess();
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Show subscription wall if no access (but allow billing page)
  const isBillingPage = location.pathname === '/app/billing';
  const showWall = !accessLoading && accessStatus && !accessStatus.has_access && !isBillingPage && user?.role !== 'super_admin';

  if (showWall) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-[#E5E5EA] shadow-lg p-8 text-center" data-testid="subscription-wall">
          <div className="w-16 h-16 bg-[#FF9500]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            {accessStatus.reason === 'blocked' ? (
              <Lock className="w-8 h-8 text-[#FF3B30]" />
            ) : (
              <AlertTriangle className="w-8 h-8 text-[#FF9500]" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-[#0A0A0A] mb-2">
            {accessStatus.reason === 'blocked' ? 'Account Suspended' :
             accessStatus.reason === 'trial_expired' ? 'Trial Expired' :
             'Subscription Required'}
          </h1>
          <p className="text-[#0A0A0A]/60 mb-6">
            {accessStatus.message || 'Please subscribe to continue using JamInventra.'}
          </p>
          <div className="space-y-3">
            <Button className="w-full bg-[#007AFF] hover:bg-[#0066D6]" onClick={() => navigate('/app/billing')} data-testid="go-to-billing-btn">
              <CreditCard className="w-4 h-4 mr-2" />
              View Plans & Subscribe
            </Button>
            <Button variant="outline" className="w-full" onClick={handleLogout} data-testid="wall-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-screen w-64 bg-white border-r border-[#E5E5EA] transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-[#E5E5EA]">
            <div>
              <Link to={user?.role === 'super_admin' ? '/app/admin' : '/app/dashboard'} className="flex items-center gap-2">
                <JamInventraLogo height={36} />
              </Link>
              <p className="text-[10px] text-[#0A0A0A]/30 mt-0.5 pl-1 tracking-wide">A product of LOM Solutions Jamaica Limited</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {getNavItems(user?.role).map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium ${
                      isActive 
                        ? 'bg-[#007AFF]/10 text-[#007AFF]' 
                        : 'text-[#0A0A0A]/70 hover:text-[#0A0A0A] hover:bg-[#F5F5F7]'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Menu */}
          <div className="p-4 border-t border-[#E5E5EA]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-between h-auto py-2 px-3 hover:bg-[#F5F5F7]" data-testid="user-menu-btn">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#007AFF] flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-[#0A0A0A]">{user?.first_name} {user?.last_name}</p>
                      <p className="text-xs text-[#0A0A0A]/60">{user?.organization_name}</p>
                    </div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#0A0A0A]/40" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/app/settings')} data-testid="menu-settings">
                  <User className="w-4 h-4 mr-2" />
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/billing')} data-testid="menu-billing">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-[#FF3B30]" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E5E5EA]">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            
            <div className="flex-1 lg:flex-none" />
            
            <div className="flex items-center gap-2">
              {user?.role === 'org_admin' && (
                <span className="text-xs font-medium bg-[#007AFF]/10 text-[#007AFF] px-2 py-1 rounded-sm">
                  Admin
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
